import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as authActions from '../../Redux/Auth/AuthActions';
import Layout from '../../Utils/Navbar/navbar-layout-component.js';
import Footer from '../../Utils/Footer/footer-component.js';
import { InputComponent } from '../../Utils/Inputs/input-component';
import { ButtonPrimary, ButtonWhite } from '../../Utils/Buttons/button-component.js';
import { getLoginData } from '../../Public/login-data.js';
import MediaComponent from '../../Utils/Media/media-component.js';
import { ASSETS } from '../../Public/assets-data.js';

function ResetPassword() {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();
    const [params]  = useSearchParams();
    const token     = params.get('token') || '';

    const { language = 'en', dir = 'ltr' } = useSelector((s) => s.systemData || {});
    const txt = getLoginData(language);

    const [newPassword,     setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error,           setError]           = useState('');
    const [done,            setDone]            = useState(false);

    useEffect(() => {
        if (!token) navigate('/login', { replace: true });
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError(txt.passwordMismatch);
            return;
        }
        const ok = await dispatch(authActions.resetPassword(token, newPassword));
        if (ok) setDone(true);
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Layout>
                <div dir={dir} className="w-full lg:h-[calc(100vh-80px)] flex flex-col lg:flex-row-reverse overflow-hidden">

                    {/* Video side */}
                    <div className="relative w-full h-[300px] lg:h-full lg:w-1/2 overflow-hidden">
                        <MediaComponent
                            video={"/placeholder.svg"}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/10 lg:bg-transparent" />

                        {/* Overlay badge */}
                        <div className="absolute bottom-8 left-8 hidden lg:flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-white/40">Security</p>
                                <p className="text-sm font-black text-white uppercase">Reset Password</p>
                            </div>
                        </div>
                    </div>

                    {/* Form side */}
                    <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 lg:p-20 overflow-y-auto bg-white">
                        <div className="w-full max-w-[400px] mx-auto">

                            {done ? (
                                /* ── Success state ── */
                                <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                                    <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6">
                                        <svg className="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                    </div>
                                    <h2
                                        className="text-4xl lg:text-5xl font-black text-primary-950 uppercase  italic leading-none mb-2"
                                        style={{ fontFamily: 'Impact, sans-serif' }}
                                    >
                                        {txt.resetTitle}
                                    </h2>
                                    <p className="text-sm font-medium text-gray-400 mb-8">
                                        {language === 'he' ? 'הסיסמה עודכנה בהצלחה' : 'Your password has been updated successfully.'}
                                    </p>
                                    <ButtonPrimary fullWidth size="md" onClick={() => navigate('/login')}>
                                        {txt.loginBtn}
                                    </ButtonPrimary>
                                </div>
                            ) : (
                                /* ── Reset form ── */
                                <>
                                    <div className={`mb-8 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                                        {/* Lock icon */}
                                        <div className="w-12 h-12 rounded-2xl bg-primary-950 flex items-center justify-center mb-6">
                                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                            </svg>
                                        </div>
                                        <h2
                                            className="text-4xl lg:text-5xl font-black text-primary-950 uppercase  italic leading-none mb-2"
                                            style={{ fontFamily: 'Impact, sans-serif' }}
                                        >
                                            {txt.resetTitle}
                                        </h2>
                                        <p className="text-sm font-medium text-gray-400 mt-2">{txt.resetSubtitle}</p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 uppercase px-1">{txt.newPasswordLabel}</label>
                                            <InputComponent
                                                dir="ltr" type="password" placeholder="••••••••"
                                                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                                className="!text-left" size="lg" required minLength={6}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-gray-500 uppercase px-1">{txt.confirmPasswordLabel}</label>
                                            <InputComponent
                                                dir="ltr" type="password" placeholder="••••••••"
                                                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="!text-left" size="lg" required
                                            />
                                        </div>

                                        {error && (
                                            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl">
                                                <svg className="w-3.5 h-3.5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                                </svg>
                                                <p className="text-xs font-bold text-red-500">{error}</p>
                                            </div>
                                        )}

                                        <ButtonPrimary type="submit" fullWidth size="md">
                                            {txt.resetBtn}
                                        </ButtonPrimary>
                                    </form>

                                    <div className="mt-6">
                                        <ButtonWhite
                                            onClick={() => navigate('/login')}
                                            fullWidth
                                            size="md"
                                            icon={
                                                <svg className={`mx-2 w-5 h-5 ${dir === 'rtl' ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                                                </svg>
                                            }
                                        >
                                            {txt.backToLogin}
                                        </ButtonWhite>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
            <Footer />
        </div>
    );
}

export default ResetPassword;
